import { Fragment as _Fragment, jsx as _jsx } from "react/jsx-runtime";
import { useProfileContext } from '@marketing/profile/ProfileContext';
import { PROFILE_STEPS } from '@marketing/profile/enums';
import { useEffect } from 'react';
import { Navigate, useNavigate } from 'react-router';
import PetAgeForm from './PetAgeForm';
import PetAgePage from './PetAgePage';
import PetBreedForm from './PetBreedForm';
import PetBreedPage from './PetBreedPage';
import PetCalculatingMealsPage from './PetCalculatingMealsPage';
import PetCurrentWeightForm from './PetCurrentWeightForm';
import PetGenderForm from './PetGenderForm';
import PetGenderPage from './PetGenderPage';
import PetHealthConcernsForm from './PetHealthConcernsForm';
import PetHealthPage from './PetHealthPage';
import PetMealsForm from './PetMealsForm';
import PetNameForm from './PetNameForm';
import PetPurchasingMotivationsForm from './PetPurchasingMotivationsForm';
import PetTargetWeightForm from './PetTargetWeightForm';
import PetWeightPage from './PetWeightPage';
/**
 * User should only be able to access pet name route if the current pet exists.
 */
var ValidPetNameRoute = function (_a) {
    var children = _a.children;
    var navigate = useNavigate();
    var _b = useProfileContext(), currentPet = _b.currentPet, profileBasePath = _b.profileBasePath;
    useEffect(function () {
        if (!currentPet) {
            console.error('Incomplete profile. Redirecting...');
            navigate(profileBasePath, { replace: true, viewTransition: true });
        }
    }, [currentPet, navigate]);
    if (!currentPet) {
        return null;
    }
    return _jsx(_Fragment, { children: children });
};
/**
 * User should only be able to access pet gender route if the current pet's name
 * has been set.
 */
var ValidPetGenderRoute = function (_a) {
    var children = _a.children;
    var navigate = useNavigate();
    var _b = useProfileContext(), currentPet = _b.currentPet, profileBasePath = _b.profileBasePath;
    useEffect(function () {
        if (!currentPet || !currentPet.name) {
            console.error('Incomplete profile. Redirecting...');
            navigate(profileBasePath, { replace: true, viewTransition: true });
        }
    }, [currentPet, navigate]);
    if (!currentPet || !currentPet.name) {
        return null;
    }
    return _jsx(_Fragment, { children: children });
};
/**
 * User should be able to access breed, age, and current weight route once gender is set.
 */
var ValidPetProfileRoute = function (_a) {
    var children = _a.children;
    var navigate = useNavigate();
    var _b = useProfileContext(), currentPet = _b.currentPet, profileBasePath = _b.profileBasePath;
    useEffect(function () {
        if (!currentPet || !currentPet.gender) {
            console.error('Incomplete profile. Redirecting...');
            navigate(profileBasePath, { replace: true, viewTransition: true });
        }
    }, [currentPet, navigate]);
    if (!currentPet || !currentPet.gender) {
        return null;
    }
    return _jsx(_Fragment, { children: children });
};
/**
 * User should be able to access target weight route once current weight is set.
 */
var ValidPetTargetWeightRoute = function (_a) {
    var children = _a.children;
    var navigate = useNavigate();
    var _b = useProfileContext(), currentPet = _b.currentPet, profileBasePath = _b.profileBasePath;
    useEffect(function () {
        if (!currentPet || !currentPet.currentWeight) {
            console.error('Incomplete profile. Redirecting...');
            navigate(profileBasePath, { replace: true, viewTransition: true });
        }
    }, [currentPet, navigate]);
    if (!currentPet || !currentPet.currentWeight) {
        return null;
    }
    return _jsx(_Fragment, { children: children });
};
/**
 * User should be able to access health concerns and calculating meals route
once weight is set.
 */
var ValidPostPetProfileRoute = function (_a) {
    var children = _a.children;
    var navigate = useNavigate();
    var _b = useProfileContext(), currentPet = _b.currentPet, profileBasePath = _b.profileBasePath;
    useEffect(function () {
        if (!currentPet || !currentPet.weight) {
            console.error('Incomplete pet profile. Redirecting...');
            navigate(profileBasePath, { replace: true, viewTransition: true });
        }
    }, [currentPet, navigate]);
    if (!currentPet || !currentPet.weight) {
        return null;
    }
    return _jsx(_Fragment, { children: children });
};
var petRoutes = [
    {
        path: 'pet',
        children: [
            {
                index: true,
                element: _jsx(Navigate, { to: "/profile/pet/0/name", replace: true }),
            },
            {
                path: ':petIndex/name',
                loader: function () { return PROFILE_STEPS.petNameForm; },
                element: (_jsx(ValidPetNameRoute, { children: _jsx(PetNameForm, {}) })),
            },
            {
                path: 'gender',
                loader: function () { return PROFILE_STEPS.petGenderPage; },
                element: (_jsx(ValidPetGenderRoute, { children: _jsx(PetGenderPage, {}) })),
            },
            {
                path: ':petIndex/gender',
                loader: function () { return PROFILE_STEPS.petGenderForm; },
                element: (_jsx(ValidPetGenderRoute, { children: _jsx(PetGenderForm, {}) })),
            },
            {
                path: 'breed',
                loader: function () { return PROFILE_STEPS.petBreedPage; },
                element: (_jsx(ValidPetProfileRoute, { children: _jsx(PetBreedPage, {}) })),
            },
            {
                path: ':petIndex/breed',
                loader: function () { return PROFILE_STEPS.petBreedForm; },
                element: (_jsx(ValidPetProfileRoute, { children: _jsx(PetBreedForm, {}) })),
            },
            {
                path: 'age',
                loader: function () { return PROFILE_STEPS.petAgePage; },
                element: (_jsx(ValidPetProfileRoute, { children: _jsx(PetAgePage, {}) })),
            },
            {
                path: ':petIndex/age',
                loader: function () { return PROFILE_STEPS.petAgeForm; },
                element: (_jsx(ValidPetProfileRoute, { children: _jsx(PetAgeForm, {}) })),
            },
            {
                path: 'weight',
                loader: function () { return PROFILE_STEPS.petWeightPage; },
                element: (_jsx(ValidPetProfileRoute, { children: _jsx(PetWeightPage, {}) })),
            },
            {
                path: ':petIndex/current_weight',
                loader: function () { return PROFILE_STEPS.petCurrentWeightForm; },
                element: (_jsx(ValidPetProfileRoute, { children: _jsx(PetCurrentWeightForm, {}) })),
            },
            {
                path: ':petIndex/target_weight',
                loader: function () { return PROFILE_STEPS.petTargetWeightForm; },
                element: (_jsx(ValidPetTargetWeightRoute, { children: _jsx(PetTargetWeightForm, {}) })),
            },
            {
                path: 'health',
                loader: function () { return PROFILE_STEPS.petHealthPage; },
                element: (_jsx(ValidPostPetProfileRoute, { children: _jsx(PetHealthPage, {}) })),
            },
            {
                path: ':petIndex/purchasing_motivations',
                loader: function () { return PROFILE_STEPS.petPurchasingMotivationsForm; },
                element: (_jsx(ValidPostPetProfileRoute, { children: _jsx(PetPurchasingMotivationsForm, {}) })),
            },
            {
                path: ':petIndex/health_concerns',
                loader: function () { return PROFILE_STEPS.petHealthConcernsForm; },
                element: (_jsx(ValidPostPetProfileRoute, { children: _jsx(PetHealthConcernsForm, {}) })),
            },
            {
                path: ':petIndex/calculating',
                loader: function () { return PROFILE_STEPS.petCalculatingMealsPage; },
                element: (_jsx(ValidPostPetProfileRoute, { children: _jsx(PetCalculatingMealsPage, {}) })),
            },
            {
                path: ':petIndex/meals',
                loader: function () { return PROFILE_STEPS.petMealsForm; },
                element: (_jsx(ValidPostPetProfileRoute, { children: _jsx(PetMealsForm, {}) })),
            },
        ],
    },
];
export default petRoutes;
